import { useEffect, useState } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { performGetRequest } from '@atlassian/jira-fetch';
import { APP_TEAM_NAME } from '@atlassian/jira-project-create-importer-common';
import { APP_IDENTIFIER } from '../../common/constants';
import type { PrioritiesState, PriorityData, PrioritiesResponse } from '../../common/types';
import { transformValue } from './utils';

export const prioritiesCache: {
	priorities: PriorityData;
} = {
	priorities: undefined,
};

export const getPriorities = async (): PrioritiesResponse => {
	const { values: priorities } = await performGetRequest(
		`${window.location.origin}/rest/api/3/priority/search`,
	);

	return priorities;
};

export const useGetPriorities = (): PrioritiesState => {
	const isCached = prioritiesCache.priorities !== undefined;

	const initialState = {
		data: prioritiesCache.priorities,
		isLoading: !isCached,
		error: null,
	};
	const [{ data, isLoading, error }, setState] = useState<PrioritiesState>(initialState);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const priorities = await getPriorities();
				if (Array.isArray(priorities) && priorities.length > 0) {
					const priorityMap = transformValue(priorities);
					prioritiesCache.priorities = priorityMap;
					setState({
						data: priorityMap,
						isLoading: false,
						error: null,
					});
				} else {
					setState({
						data: undefined,
						isLoading: false,
						error: null,
					});
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useGetPriorities',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					error: err,
					sendToPrivacyUnsafeSplunk: true,
				});
				setState({
					data: undefined,
					isLoading: false,
					error: err,
				});
			}
		};

		if (!isCached) {
			fetchData();
		}
	}, [isCached]);

	return { data, isLoading, error };
};
